import * as _ from "lodash";

import { Bar, BarData, ChartEntity, DataPointsMeta } from "@ctra/api";
import { Optional } from "@ctra/utils";

import { ChartContextType } from "../../../../providers";
import { SeriesType } from "../../../../typings";
import { filterBarBySeries } from "../../../../utils";

export class BarChartProcessor {
  public identity = "BarChartProcessor";

  /**
   * Tell if the bar chart may be grouped
   * @param {ChartContextType<BarData>["values"]["meta"]} meta
   */
  mayGroup(meta: ChartContextType<BarData>["values"]["meta"]) {
    return false;
  }

  /**
   * Filter unwanted items from the line data (the info which does not belong to any series)
   * @param {ChartContextType<BarData>["values"]["data"]} data
   * @param {ChartContextType<BarData>["values"]["meta"]} meta
   * @param {ChartContextType<BarData>["values"]["series"]} series
   * @returns {Record<ChartEntity["id"], Optional<Bar>>}
   */
  getFilteredRawData(
    data: ChartContextType<BarData>["values"]["data"],
    meta: ChartContextType<BarData>["values"]["meta"],
    series: ChartContextType<BarData>["values"]["series"]
  ): Record<ChartEntity["id"], Optional<Bar>> {
    return _.mapValues(data, (barCollection, key) => {
      if (!!barCollection) {
        const barCollectionMeta: Optional<DataPointsMeta> = _.get(meta, [key]);

        if (!!barCollectionMeta) {
          return filterBarBySeries(barCollection, barCollectionMeta, series);
        }

        return barCollection;
      }

      return barCollection;
    });
  }

  /**
   * Same as filtered data
   * @param {ChartContextType<BarData>["values"]["data"]} data
   * @param {ChartContextType<BarData>["values"]["meta"]} meta
   * @param {ChartContextType<BarData>["values"]["series"]} series
   * @returns {ChartContextType<BarData>["values"]["data"]}
   */
  getConsolidatedData(
    data: ChartContextType<BarData>["values"]["data"],
    meta: ChartContextType<BarData>["values"]["meta"],
    series: ChartContextType<BarData>["values"]["series"]
  ): ChartContextType<BarData>["values"]["data"] {
    return this.getFilteredRawData(data, meta, series);
  }

  /**
   * Remove the undefined values from the filtered data
   * @param {ChartContextType<BarData>["values"]["data"]} data
   * @param {ChartContextType<BarData>["values"]["meta"]} meta
   * @param {ChartContextType<BarData>["values"]["series"]} series
   * @returns {Bar}
   */
  getData(
    data: ChartContextType<BarData>["values"]["data"],
    meta: ChartContextType<BarData>["values"]["meta"],
    series: ChartContextType<BarData>["values"]["series"]
  ): Bar {
    return _.flatMap(_.omitBy(this.getConsolidatedData(data, meta, series), _.isUndefined)) as Bar;
  }

  /**
   * Return the series, to implement in subclasses
   * @param {ChartContextType<BarData>["values"]["meta"]} meta
   * @param {ChartContextType<BarData>["values"]["series"]} series
   * @returns {SeriesType}
   */
  getSeries(
    meta: ChartContextType<BarData>["values"]["meta"],
    series: ChartContextType<BarData>["values"]["series"]
  ): SeriesType {
    return series;
  }
}
